// DealersLanding.js
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Button, Form, Alert, Modal, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faQrcode,
  faHandshake,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { LeadsClient } from "../web-api-client.ts";
import { useNavigate } from "react-router-dom";

const initialFormState = {
  name: "",
  dealership: "",
  email: "",
  phone: "",
  message: "",
};

const DealersLanding = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [showDemoModal, setShowDemoModal] = useState(false);

  const openDemoModal = () => {
    setError(null);
    setSubmitted(false);
    setShowDemoModal(true);
  };
  const navToLogin = () => {
    window.location.href = '/Identity/Account/Login?ReturnUrl=/dashboard';
  };

  const closeDemoModal = () => setShowDemoModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = useMemo(() => {
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email || "");
    const nameOk = !!form.name.trim();
    const dealerOk = !!form.dealership.trim();
    return { emailOk, nameOk, dealerOk };
  }, [form.email, form.name, form.dealership]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitted(false);

    if (!validate.nameOk) return setError("Please enter your name.");
    if (!validate.dealerOk) return setError("Please enter your dealership name.");
    if (!validate.emailOk) return setError("Please enter a valid email address.");

    setSubmitting(true);

    try {
      const client = new LeadsClient();

      const dto = {
        id: undefined,
        name: form.name,
        dealer: form.dealership,
        email: form.email,
        phone: form.phone,
        message: form.message,
        internalNotes: null,
        created: undefined,
        isArchived: false,
      };

      const ok = await client.postApiLeadsAdmin(dto);

      if (ok) {
        setSubmitted(true);
        setForm(initialFormState);
      } else {
        setError("Something went wrong sending your message. Please try again.");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Dealer lead submission error:", err);
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderContactForm = (compact = false) => (
    <>
      <Form onSubmit={handleSubmit}>
        <Row className={compact ? "g-2" : "g-3"}>
          {[
            { id: "name", label: "Your Name", placeholder: "Jane Doe" },
            { id: "dealership", label: "Dealership", placeholder: "Acme Auto Group" },
            { id: "email", label: "Email", placeholder: "you@dealership.com", type: "email" },
            { id: "phone", label: "Phone", placeholder: "(555) 123-4567", type: "tel", required: false },
          ].map((f) => (
            <Col md={6} key={f.id}>
              <Form.Group controlId={`contact-${compact ? "modal-" : ""}${f.id}`}>
                <Form.Label className="qcar-label">{f.label}</Form.Label>
                <Form.Control
                  type={f.type || "text"}
                  name={f.id}
                  value={form[f.id]}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                  className="qcar-input"
                  required={f.required !== false && f.id !== "phone"}
                  disabled={submitting}
                  autoComplete="on"
                />
              </Form.Group>
            </Col>
          ))}

          <Col xs={12}>
            <Form.Group controlId={`contact-${compact ? "modal-" : ""}message`}>
              <Form.Label className="qcar-label">What are you hoping to improve?</Form.Label>
              <Form.Control
                as="textarea"
                rows={compact ? 3 : 4}
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your inspection or sales workflow."
                className="qcar-input"
                disabled={submitting}
              />
            </Form.Group>
          </Col>

          <Col xs={12} className="d-flex justify-content-end gap-2">
            {compact && (
              <Button type="button" variant="outline-light" onClick={closeDemoModal} disabled={submitting}>
                Close
              </Button>
            )}
            <Button type="submit" className="qcar-btn qcar-btn-solid" disabled={submitting}>
              {submitting ? "Sending..." : "Send Message"}
            </Button>
          </Col>
        </Row>
      </Form>

      {submitted && (
        <Alert variant="success" className="mt-3 py-2 small mb-0">
          Thanks! We’ve received your message.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mt-3 py-2 small mb-0">
          {error}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <style>{`
        :root{
          --qcar-bg: #0b0f12;
          --qcar-bg2:#0f1419;
          --qcar-text: rgba(255,255,255,0.92);
          --qcar-muted: rgba(255,255,255,0.62);
          --qcar-green: #4f8f4c;
          --qcar-green2:#3f7f46;
          --qcar-border: rgba(255,255,255,0.12);
          --qcar-shadow: 0 22px 70px rgba(0,0,0,0.55);
        }

        .qcar-page{
          background: radial-gradient(1200px 600px at 10% 0%, rgba(79,143,76,0.18), transparent 60%),
                      radial-gradient(1000px 600px at 90% 20%, rgba(255,255,255,0.06), transparent 55%),
                      linear-gradient(180deg, var(--qcar-bg), var(--qcar-bg2));
          color: var(--qcar-text);
          min-height: 100vh;
          width: 100vw;
          max-width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
        }

        /* Top nav */
        .qcar-topbar{
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(10,14,18,0.72);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-bottom: 1px solid rgba(255,255,255,0.10);
        }
        .qcar-brand{
          display:flex;
          align-items:center;
          gap:10px;
        }
        .qcar-brand img{
          height: 75px;
          width: auto;
          display:block;
        }
        .qcar-nav a{
          color: rgba(255,255,255,0.75);
          text-decoration:none;
          font-size: 0.95rem;
          font-weight: 500;
        }
        .qcar-nav a:hover{
          color: rgba(255,255,255,0.95);
        }

        /* Buttons */
        .qcar-btn{
          border-radius: 4px !important;
          padding: 0.7rem 1.15rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.2px;
          border: 1px solid rgba(255,255,255,0.14) !important;
        }
        .qcar-btn-solid{
          background: linear-gradient(180deg, var(--qcar-green), var(--qcar-green2)) !important;
          border-color: rgba(0,0,0,0.18) !important;
          color: #fff !important;
          box-shadow: 0 14px 40px rgba(79,143,76,0.24);
        }
        .qcar-btn-solid:hover{
          filter: brightness(1.04);
          transform: translateY(-1px);
        }
        .qcar-btn-ghost{
          background: rgba(0,0,0,0.12) !important;
          color: rgba(255,255,255,0.90) !important;
          border: 1px solid rgba(255,255,255,0.16) !important;
        }
        .qcar-btn-ghost:hover{
          background: rgba(255,255,255,0.08) !important;
        }

        /* Shared hero backdrop that extends behind How It Works */
        .qcar-hero-backdrop{
          position: relative;
          overflow: hidden;
        }

        /* Base dark background (no image on the left) */
        .qcar-hero-backdrop::before{
          content:"";
          position:absolute;
          inset:0;
          background:
            radial-gradient(1000px 520px at 10% 10%, rgba(79,143,76,0.12), transparent 60%),
            linear-gradient(180deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.40) 55%, rgba(0,0,0,0.28) 100%),
            linear-gradient(180deg, var(--qcar-bg), var(--qcar-bg2));
          z-index: 0;
        }
          .qcar-page{
  overflow-x: hidden; /* prevents any stray horizontal scroll */
}

/* If you used qcar-hero-inner above, this replaces ms-5 */
@media (min-width: 992px){
  .qcar-hero-inner{
    padding-left: 3rem;
  }
}

        /* Right-side image spanning BOTH hero + how-it-works */
        .qcar-hero-backdrop::after{
  content:"";
  position:absolute;
  top:0;
  right:0;
  height:100%;
  width:60%; /* image occupies ~60% of the width */
  z-index:0;

  /* Rounded vignette + subtle lens darkening + image */
  background:
    /* Rounded bottom vignette (curved fade) */
    radial-gradient(
      ellipse 120% 60% at 60% 100%,
      rgba(0,0,0,0.88) 0%,
      rgba(0,0,0,0.62) 38%,
      rgba(0,0,0,0.28) 62%,
      rgba(0,0,0,0.00) 80%
    ),

    /* Global vignette / lens falloff */
    radial-gradient(
      ellipse 140% 90% at 70% 40%,
      rgba(0,0,0,0.00) 0%,
      rgba(0,0,0,0.18) 65%,
      rgba(0,0,0,0.42) 100%
    ),

    /* Hero image */
    url("/dealer-hero.png");

  background-size: cover;
  background-position: right center;
  background-repeat: no-repeat;

  /* Fade image into the left dark background */
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0.00) 0%,
    rgba(0,0,0,0.35) 14%,
    rgba(0,0,0,0.75) 30%,
    rgba(0,0,0,1.00) 48%,
    rgba(0,0,0,1.00) 100%
  );
  mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0.00) 0%,
    rgba(0,0,0,0.35) 14%,
    rgba(0,0,0,0.75) 30%,
    rgba(0,0,0,1.00) 48%,
    rgba(0,0,0,1.00) 100%
  );

  filter: saturate(0.92) contrast(1.02);
  opacity: 0.96;
}


        /* Hero layout */
        .qcar-hero{
          position: relative;
          padding: 56px 0 22px 0;
          
          z-index: 1;
        }

        /* Headings */
        .qcar-hero-title{
          font-size: clamp(2.0rem, 3.6vw, 3.0rem);
          font-weight: 600;
          line-height: 1.08;
          letter-spacing: -0.4px;
          text-shadow: 0 10px 40px rgba(0,0,0,0.55);
        }
        .qcar-hero-sub{
          max-width: 720px;
          color: var(--qcar-muted);
          font-size: 1.05rem;
          margin-top: 14px;
          font-weight: 400;
        }

        .qcar-hero-actions{
          margin-top: 22px;
          display:flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        /* Hero strip: inline, no borders */
        .qcar-hero-strip{
          margin-top: 22px;
          display:flex;
          gap: 46px;
          flex-wrap: wrap;
          color: rgba(255,255,255,0.70);
          font-weight: 500;
          font-size: 0.98rem;
        }
        .qcar-hero-strip span{
          display:inline-flex;
          align-items:center;
          gap: 10px;
          padding: 0;
          border-radius: 0;
          background: transparent;
          border: none;
        }
        .qcar-hero-strip .dot{
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--qcar-green);
          box-shadow: 0 0 0 5px rgba(79,143,76,0.15);
        }

        /* Sections */
        .qcar-section{
          padding: 44px 0;
        }
        .qcar-section-title{
          font-size: 1.9rem;
          font-weight: 600;
          letter-spacing: -0.2px;
          margin-bottom: 6px;
          text-align:center;
        }
        .qcar-section-sub{
          text-align:center;
          color: var(--qcar-muted);
          margin-bottom: 22px;
          font-weight: 400;
        }
.qcar-list-wrap{
  text-align: left;
}

@media (min-width: 992px){
  .qcar-split .row{
    column-gap: 80px; /* more like the mock */
  }
}
        /* How it works */
        .qcar-how{
          position: relative;
          z-index: 1;
          padding-top: 26px;
        }
        .qcar-how > .container{
          position: relative;
          z-index: 2;
        }

        /* Optional #4: soften the panel so it feels like it floats */
        .qcar-hiw{
          background: rgba(0,0,0,0.95);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 6px;
          padding: 22px 14px;
          box-shadow: 0 18px 60px rgba(0,0,0,0.35);
        }
        .qcar-hiw-step{
          display:flex;
          flex-direction: column;
          align-items:center;
          justify-content:center;
          text-align:center;
          gap: 10px;
          padding: 14px 10px;
          height: 100%;
        }
        .qcar-hiw-icon{
          width: 118px;
          height: 118px;
          border-radius: 12px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(79,143,76,0.10);
          border: 1px solid rgba(79,143,76,0.22);
        }
        .qcar-hiw-label{
          font-weight: 600;
          letter-spacing: -0.1px;
          font-size: 1.02rem;
        }
        .qcar-hiw-sub{
          color: rgba(255,255,255,0.58);
          font-size: 0.92rem;
          font-weight: 400;
        }
        .qcar-hiw-arrow{
          opacity: 0.55;
          font-size: 1.4rem;
          user-select:none;
          display:flex;
          align-items:center;
          justify-content:center;
          height: 100%;
        }

        /* Optional #4: fade the backdrop out AFTER how-it-works */
        .qcar-how::after{
          content:"";
          position:absolute;
          left:0;
          right:0;
          bottom:-1px;
          height: 160px;
          background: linear-gradient(180deg, rgba(0,0,0,0.00), var(--qcar-bg2));
          z-index: 1;
          pointer-events:none;
        }
.qcar-hiw{
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
}
        /* Proof Replaces Pressure: accent lines */
        .qcar-tagline-wrap{
          display:flex;
          align-items:center;
          justify-content:center;
          gap: 18px;
          margin-top: 16px;
          color: rgba(255,255,255,0.62);
          font-style: italic;
          letter-spacing: 0.3px;
        }
        .qcar-tagline-line{
          height: 1px;
          width: 110px;
          background: rgba(255,255,255,0.18);
        }

        /* For Dealerships / For Buyers */
        .qcar-split{
          margin-top: 26px;
        }
        .qcar-split h3{
          font-size: 1.15rem;
          font-weight: 600;
          margin-bottom: 12px;
        }
        .qcar-checklist{
          list-style:none;
          padding-left: 0;
          margin: 0;
        }
        .qcar-checklist li{
          display:flex;
          gap: 10px;
          align-items:flex-start;
          padding: 8px 0;
          color: rgba(255,255,255,0.74);
          font-weight: 400;
        }
        .qcar-check{
          color: var(--qcar-green);
          margin-top: 3px;
        }
          .qcar-hiw-rail{
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
}

        /* Pricing */
        .qcar-pricing{
          text-align:center;
          background: rgba(0,0,0,0.26);
          border-top: 1px solid rgba(255,255,255,0.10);
          border-bottom: 1px solid rgba(255,255,255,0.10);
        }
        .qcar-pricing h2{
          font-size: 1.8rem;
          font-weight: 600;
          letter-spacing: -0.2px;
          margin-bottom: 8px;
        }
        .qcar-pricing p{
          color: var(--qcar-muted);
          margin-bottom: 8px;
          font-weight: 400;
        }
        .qcar-pricing .qcar-italic{
          margin-top: 10px;
          color: rgba(255,255,255,0.62);
          font-style: italic;
          font-weight: 400;
        }
          .qcar-tagline-wrap{
  padding-top: 10px;
  padding-bottom: 2px;
}

        /* Bottom CTA */
        .qcar-bottom-cta{
          text-align:center;
          padding: 44px 0 56px 0;
        }
        .qcar-bottom-cta h2{
          font-size: 2.0rem;
          font-weight: 600;
          letter-spacing: -0.2px;
          margin-bottom: 8px;
        }
        .qcar-bottom-cta p{
          color: var(--qcar-muted);
          margin-bottom: 18px;
          font-weight: 400;
        }

        /* Inputs */
        .qcar-label{
          color: rgba(255,255,255,0.80) !important;
          font-weight: 500;
        }
        .qcar-input{
          background: rgba(255,255,255,0.06) !important;
          color: rgba(255,255,255,0.92) !important;
          border: 1px solid rgba(255,255,255,0.12) !important;
          border-radius: 6px !important;
        }
        .qcar-input::placeholder{
          color: rgba(255,255,255,0.45);
        }
        .qcar-input:focus{
          box-shadow: 0 0 0 0.2rem rgba(79,143,76,0.22) !important;
          border-color: rgba(79,143,76,0.35) !important;
        }

        /* Modal */
        .demo-modal-dialog{
          width: auto !important;
          max-width: 720px !important;
          margin: 0.75rem auto;
        }
        .demo-modal-content{
          border-radius: 10px;
          overflow: hidden;
          background: #0f1419 !important;
          color: rgba(255,255,255,0.92) !important;
          border: 1px solid rgba(255,255,255,0.12) !important;
          box-shadow: var(--qcar-shadow);
        }
        .demo-modal-header{
          background: #0f1419 !important;
          color: rgba(255,255,255,0.92) !important;
          border-bottom: 1px solid rgba(255,255,255,0.10) !important;
        }
        .demo-modal-body{
          background: #0f1419 !important;
          color: rgba(255,255,255,0.92) !important;
        }
        .demo-modal-close{
          color: rgba(255,255,255,0.75) !important;
          text-decoration: none;
          font-size: 1.25rem;
          line-height: 1;
        }
        .demo-modal-close:hover{ color: rgba(255,255,255,0.95) !important; }

        @media (max-width: 991.98px){
          .qcar-hero-backdrop::after{
            width: 62%;
            background-position: 78% center;
          }
        }

        @media (max-width: 575.98px){
          .qcar-hero{
            min-height: 60vh;
          }

          .qcar-hero-backdrop::after{
            width: 100%;
            opacity: 0.65;
            background-position: 75% center;
          }

          .qcar-hero-strip{
            gap: 14px;
          }

          .qcar-hiw-arrow{
            display:none;
          }

          .demo-modal-dialog{
            width: auto !important;
            max-width: calc(100vw - 28px) !important;
            margin: 14px !important;
            box-sizing: border-box;
          }
          .demo-modal-content{
            width: 100% !important;
            max-width: 100% !important;
            max-height: 84vh;
            border-radius: 10px;
            box-sizing: border-box;
          }
          .demo-modal-body{
            max-height: calc(84vh - 64px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 18px;
          }
          .modal{ padding-right: 0 !important; }
          .modal-backdrop.show{ opacity: 0.45; }
          .modal{
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
          }
        }
          @media (max-width: 575.98px){
  /* Smaller logo so topbar doesn't eat vertical space */
  .qcar-brand img{
    height: 44px; /* was 75px */
  }

  /* Make hero tighter so it doesn't feel like it runs forever */
  .qcar-hero{
    padding: 18px 0 10px 0; /* was 56px 0 22px 0 */
  }
    @media (min-width: 992px){
  .qcar-hero-inner{
    padding-left: 4.5rem; /* was 3rem */
    padding-right: 2.0rem;
  }
}

  /* Reduce section padding so How It Works starts sooner */
  .qcar-section{
    padding: 22px 0; /* was 44px 0 */
  }
  .qcar-how{
    padding-top: 8px; /* was 26px */
  }

  /* Pull How It Works title up closer */
  .qcar-section-title{
    margin-bottom: 2px;
  }

  /* Tighten space between hiw panel and the next content */
  .qcar-hiw{
    margin-top: 10px !important; /* overrides mt-3 feel */
    padding: 14px 10px;
  }

  /* Optional: prevent big buttons from stretching layout */
  .qcar-hero-actions .qcar-btn{
    width: auto;
  }
}
 /* Center the whole panel consistently */
.qcar-hiw-rail{
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
}

/* True centering + symmetric spacing */
.qcar-hiw{
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
}

/* The actual 3-step layout */
.qcar-hiw-grid{
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center;
  gap: 28px;
}

/* Keep steps centered and same visual weight */
.qcar-hiw-step{
  display:flex;
  flex-direction: column;
  align-items:center;
  justify-content:center;
  text-align:center;
  gap: 10px;
  padding: 18px 10px;
}

/* Bigger icon container */
.qcar-hiw-icon{
  width: 92px;
  height: 92px;
  border-radius: 10px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(79,143,76,0.10);
  border: 1px solid rgba(79,143,76,0.22);
}

/* Make the image fill the icon box nicely */
.qcar-hiw-img{
  width: 96px;
  height: 96px;
  object-fit: contain;
  display:block;
}

/* Arrows centered between columns */
.qcar-hiw-arrow{
  opacity: 0.55;
  font-size: 1.6rem;
  user-select:none;
  line-height: 1;
  display:flex;
  align-items:center;
  justify-content:center;
}

/* Mobile: stack steps, hide arrows */
@media (max-width: 767.98px){
  .qcar-hiw-grid{
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .qcar-hiw-arrow{
    display:none;
  }
  .qcar-hiw-step{
    padding: 12px 8px;
  }
  .qcar-hiw-icon{
    width: 104px;
    height: 104px;
  }
  .qcar-hiw-img{
    width: 84px;
    height: 84px;
  }
}

      `}</style>

      <div className="qcar-page">
        {/* TOP BAR */}
        <div className="qcar-topbar">
          <Container fluid className="py-2 px-3 px-md-4">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="qcar-brand">
                <img src="logo-light.png" alt="Q-caR" />
              </div>

              <div className="d-none d-md-flex align-items-center gap-3 qcar-nav">
                <Nav className="gap-3">
                  <Nav.Item>
                    <Nav.Link as="a" onClick={() => scrollToId("how-it-works")}>
                      How it Works
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as="a" onClick={() => scrollToId("pricing")}>
                      Pricing
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link as="a" onClick={() => scrollToId("contact")}>
                      Contact
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button className="qcar-btn qcar-btn-solid" onClick={navToLogin}>
                  Login
                </Button>
                <Button className="qcar-btn qcar-btn-solid" onClick={openDemoModal}>
                  Book a Demo
                </Button>
              </div>
            </div>
          </Container>
        </div>

        {/* HERO + HOW IT WORKS BACKDROP WRAPPER */}
        <div className="qcar-hero-backdrop">
          {/* HERO */}
          <section className="qcar-hero">
            <Container fluid className="px-3 px-md-4 qcar-hero-inner">
              <Row className="align-items-center">
                <Col lg={7}>
                  <div className="qcar-hero-title">Transparency That Sells Cars Faster</div>
                  <div className="qcar-hero-sub">
                    Turn vehicle inspections into scannable QR codes that build buyer trust, reduce objections, and
                    generate real-time leads.
                  </div>

                  <div className="qcar-hero-actions">
                    <Button className="qcar-btn qcar-btn-solid" onClick={openDemoModal}>
                      Book a Demo
                    </Button>
                    <Button className="qcar-btn qcar-btn-ghost" onClick={() => scrollToId("how-it-works")}>
                      See How It Works
                    </Button>
                  </div>

                  <div className="qcar-hero-strip">
                    <span>
                      <span className="dot" /> Built For Dealers. By Dealers.
                    </span>
                    <span>
                      <span className="dot" /> Increase Sales & Reputation
                    </span>
                    <span>
                      <span className="dot" /> No Change to Your Inspection Process
                    </span>
                  </div>
                </Col>

                <Col lg={5} className="d-none d-lg-block" />
              </Row>
            </Container>
          </section>

          {/* HOW IT WORKS */}
          <section id="how-it-works" className="qcar-section qcar-how">
            <Container>
              <div className="qcar-hiw-rail">


                <div className="qcar-section-title">How It Works</div>

                <div className="qcar-hiw mt-3">
                  <div className="qcar-hiw-grid">
                    <div className="qcar-hiw-step">
                      <div className="qcar-hiw-icon">
                        <img src="/clipboard-icon.png" alt="Clipboard Icon" className="qcar-hiw-img" />
                      </div>
                      <div className="qcar-hiw-label">Upload Your Inspection</div>
                      <div className="qcar-hiw-sub">Photos, notes, and statuses</div>
                    </div>

                    <div className="qcar-hiw-arrow">›</div>

                    <div className="qcar-hiw-step">
                      <div className="qcar-hiw-icon">
                        <img src="/qr-icon.png" alt="QR Code Icon" className="qcar-hiw-img" />
                      </div>
                      <div className="qcar-hiw-label">Generate a QR Code</div>
                      <div className="qcar-hiw-sub">Scan from the lot & Online</div>
                    </div>

                    <div className="qcar-hiw-arrow">›</div>

                    <div className="qcar-hiw-step">
                      <div className="qcar-hiw-icon">
                        <img src="/handshake-icon.png" alt="Handshake Icon" className="qcar-hiw-img" />
                      </div>
                      <div className="qcar-hiw-label">Build Trust & Close Faster</div>
                      <div className="qcar-hiw-sub">Fewer objections</div>
                    </div>
                  </div>

                  <div className="qcar-tagline-wrap">
                    <div className="qcar-tagline-line" />
                    <div>Proof Replaces Pressure.</div>
                    <div className="qcar-tagline-line" />
                  </div>

                </div>
              </div>


            </Container>


          </section>

        </div>
        <section id="dealer-split" className="qcar-section qcar-split">
          <Container>
            <div className="qcar-split">
              <Row className="g-4 justify-content-center">
                <Col md={5} lg={4}>
                  <div className="qcar-list-wrap">
                    <h3>For Dealerships</h3>
                    <ul className="qcar-checklist">
                      {[
                        "Build instant trust",
                        "Reduce objections & limit comebacks",
                        "Keep buyers on the lot",
                        "Justify your asking price",
                        "Real-time lead alerts & data collection",
                        "Differentiate every vehicle",
                        "Elite sales tool"
                      ].map((t) => (
                        <li key={t}>
                          <FontAwesomeIcon icon={faCheck} className="qcar-check" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>

                <Col md={5} lg={4}>
                  <div className="qcar-list-wrap">
                    <h3>For Buyers</h3>
                    <ul className="qcar-checklist">
                      {[
                        "See the car’s true current condition",
                        "Photos of hard-to-see areas",
                        "Less pressure, more confidence",
                        "All-in-one sources for vehicle info",
                        "Faster decision-making",
                        "Upfront transparency",
                      ].map((t) => (
                        <li key={t}>
                          <FontAwesomeIcon icon={faCheck} className="qcar-check" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        </section>

        {/* PRICING */}
        <section id="pricing" className="qcar-section qcar-pricing">
          <Container>
            <h2>Simple, Scalable Pricing</h2>
            <p>Plans are based on inventory size.</p>
            <p>Discounts for annual prepay and multi-location dealers.</p>
            <div className="mt-3">
              <Button className="qcar-btn qcar-btn-solid" onClick={openDemoModal}>
                Request Pricing
              </Button>
            </div>
          </Container>
        </section>

        {/* FAQ (commented out for now) */}
        {/*
        <section className="qcar-section" style={{ paddingTop: 10 }}>
          <Container>
            <div className="qcar-section-title">FAQs</div>
            <div className="qcar-section-sub">Quick answers before you book a demo.</div>
            ...
          </Container>
        </section>
        */}

        {/* CONTACT */}
        <section id="contact" className="qcar-section">
          <Container>
            <div className="qcar-section-title">Get in touch</div>
            <div className="qcar-section-sub">Tell us about your dealership — we'll reach out soon.</div>

            <Row className="g-4 justify-content-center">
              <Col md={8}>
                <div
                  style={{
                    background: "rgba(0,0,0,0.22)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    borderRadius: 6,
                    padding: 18,
                    boxShadow: "0 18px 60px rgba(0,0,0,0.30)",
                  }}
                >
                  {renderContactForm(false)}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* BOTTOM CTA */}
        <section className="qcar-bottom-cta">
          <Container>
            <h2>Sell With Proof. Not Pressure.</h2>
            <p>Join dealerships using transparency to close deals faster.</p>
            <Button className="qcar-btn qcar-btn-solid" onClick={openDemoModal}>
              Schedule a Demo
            </Button>
          </Container>
        </section>

        {/* DEMO MODAL */}
        <Modal
          show={showDemoModal}
          onHide={closeDemoModal}
          centered
          dialogClassName="demo-modal-dialog"
          contentClassName="demo-modal-content"
          backdrop
          keyboard
        >
          <Modal.Header className="demo-modal-header">
            <div className="d-flex align-items-center justify-content-between w-100">
              <div className="pe-2">
                <div className="fw-semibold">Request a demo</div>
                <div className="text-muted small" style={{ color: "rgba(255,255,255,0.62)" }}>
                  Tell us about your dealership — we’ll reach out soon.
                </div>
              </div>
              <Button
                variant="link"
                className="p-0 demo-modal-close"
                onClick={closeDemoModal}
                aria-label="Close"
                title="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>
          </Modal.Header>
          <Modal.Body className="demo-modal-body">{renderContactForm(true)}</Modal.Body>
        </Modal>

        {/* FOOTER */}
        <footer
          className="py-4 text-center"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.10)",
            color: "rgba(255,255,255,0.55)",
            background: "rgba(0,0,0,0.18)",
          }}
        >
          <Container>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src="logo-light.png" alt="Q-caR" style={{ height: 22, width: "auto" }} />
              </div>

              <div className="small" style={{ color: "rgba(255,255,255,0.55)" }}>
                Privacy Policy &nbsp; | &nbsp; Terms of Service &nbsp; | &nbsp; Contact
              </div>

              <div className="small">© {new Date().getFullYear()} Q-caR. All rights reserved.</div>
            </div>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default DealersLanding;
